/**
 * Chorégraphie du compte à rebours (Web Animations API), sans état : le
 * client (countdown-timer.js) décide quand appeler chaque étape.
 *
 * Décompte final façon nouvel an :
 * 1. en amont du « 10 », passage de relais : le fond de la carte flip devient
 *    transparent, le libellé se replie, puis la carte cède la place à un
 *    chiffre nu (mêmes métriques CSS, échange invisible). En parallèle, la place
 *    du grand chiffre est réservée et le bloc monte, progressivement ;
 * 2. au « 10 », le chiffre grossit dans la place déjà réservée (aucun saut) et
 *    chaque seconde « claque » (bounce ancré en haut : ne recouvre jamais le
 *    texte au-dessus).
 * Puis ouverture : fondu, redescente, cercle effacé, logo agrandi.
 *
 * Transcription de dpc-timer : src/components/organisms/CountdownTimer.motion.ts
 */
const EASE_OUT_SOFT = "cubic-bezier(0.22, 1, 0.36, 1)";
const EASE_IN_OUT_SOFT = "cubic-bezier(0.65, 0, 0.35, 1)";
/** Durée nulle quand l'animation ne doit pas être jouée (chargement, mouvement réduit). */
const withMotion = (options, animate) => (animate ? options : { ...options, duration: 0, delay: 0 });
// --- Chute d'une unité expirée -------------------------------------------------
const FALL_KEYFRAMES = [
  { transform: "translateY(0)", opacity: 1 },
  { transform: "translateY(100vh)", opacity: 0 },
];
const FALL = {
  duration: 1100,
  easing: "cubic-bezier(0.55, 0, 1, 0.45)",
  fill: "forwards",
};
const RECENTER = {
  duration: 700,
  easing: EASE_OUT_SOFT,
};
/**
 * Retire une unité et son séparateur : chute, puis recentrage des survivants
 * (FLIP : on mesure avant/après le retrait et on anime l'écart).
 */
export async function collapseUnit(row, unit, animate) {
  const leaving = [
    row.querySelector(`[data-duration-unit="${unit}"]`),
    row.querySelector(`[data-separator="${unit}"]`),
  ].filter((el) => el !== null);
  if (animate) {
    await Promise.all(leaving.map((el) => el.animate(FALL_KEYFRAMES, FALL).finished));
  }
  const survivors = [...row.children].filter((el) => el instanceof HTMLElement && !leaving.includes(el));
  const leftBefore = survivors.map((el) => el.getBoundingClientRect().left);
  for (const el of leaving) el.remove();
  if (!animate) return;
  survivors.forEach((el, index) => {
    const dx = (leftBefore[index] ?? 0) - el.getBoundingClientRect().left;
    if (dx !== 0) el.animate([{ transform: `translateX(${dx}px)` }, { transform: "none" }], RECENTER);
  });
}
// --- Décompte final --------------------------------------------------------------
// Chiffre nu : mêmes métriques que la carte flip (variables --digit-* de src/countdown.css).
const FINAL_NUMBER_CLASS =
  "inline-flex h-(--digit-height) min-w-(--digit-width) items-center justify-center font-sans text-(length:--digit-size) font-light leading-none tabular-nums text-white";
const FINAL_FONT_VW = 0.35;
const FINAL_FONT_MAX_PX = 176;
const HANDOFF = {
  duration: 450,
  easing: "ease-in-out",
  fill: "forwards",
};
// Montée progressive + réservation de place, lancées avec le passage de relais.
const PREPARE_MS = 1800;
const PREPARE = {
  duration: PREPARE_MS,
  easing: EASE_IN_OUT_SOFT,
  fill: "forwards",
};
// Respiration entre la fin de la montée et le « 10 ».
const FLOW_MARGIN_MS = 300;
/** Avance (ms) avec laquelle lancer la préparation pour qu'elle soit finie au « 10 ». */
export const FINAL_LEAD_MS = PREPARE_MS + FLOW_MARGIN_MS;
// Pas de rebond ici : le chiffre ne doit jamais dépasser la place réservée.
const ENLARGE = {
  duration: 550,
  easing: EASE_OUT_SOFT,
  fill: "forwards",
};
// Bounce violent : jaillit très vite, puis retombe avec rebond.
const BOUNCE_PEAK_SCALE = 2.2;
const BOUNCE_KEYFRAMES = [
  { transform: "scale(1)", easing: "cubic-bezier(0.1, 0.9, 0.2, 1)" },
  {
    transform: `scale(${BOUNCE_PEAK_SCALE})`,
    offset: 0.18,
    easing: "cubic-bezier(0.34, 1.4, 0.64, 1)",
  },
  { transform: "scale(1)" },
];
const BOUNCE = { duration: 900 };
// Marge minimale entre le haut du bloc monté et le haut de l'écran.
const VIEWPORT_MARGIN_PX = 24;
/** Taille de police du grand chiffre — mesurée une seule fois par décompte. */
export const finalFontPx = () => Math.min(window.innerWidth * FINAL_FONT_VW, FINAL_FONT_MAX_PX);
/**
 * Réserve la hauteur du grand chiffre et monte le bloc de la moitié du débord
 * du bounce : tout le déplacement vertical a lieu avant le « 10 ».
 * La hauteur de boîte (en em) est mesurée sur la carte, qui partage les
 * métriques du chiffre nu.
 */
export function prepareFinalSpace(block, card, lifted, fontPx, animate) {
  const boxEm = card.offsetHeight / Number.parseFloat(getComputedStyle(card).fontSize);
  const boxPx = boxEm * fontPx;
  // Plafond (surtout mobile) : le recentrage dû à la place réservée fait déjà
  // monter le bloc de growth / 2 ; la montée ne doit pas sortir le haut du
  // bloc de l'écran.
  const growth = boxPx - block.offsetHeight;
  const maxLiftPx = Math.max(0, lifted.getBoundingClientRect().top - growth / 2 - VIEWPORT_MARGIN_PX);
  const liftPx = Math.min(((BOUNCE_PEAK_SCALE - 1) * boxPx) / 2, maxLiftPx);
  const options = withMotion(PREPARE, animate);
  block.animate([{ minHeight: `${block.offsetHeight}px` }, { minHeight: `${boxPx}px` }], options);
  lifted.animate([{ transform: "translateY(0)" }, { transform: `translateY(${-liftPx}px)` }], options);
}
/**
 * Passage de relais : fond de la carte → transparent, libellé replié, puis
 * chiffre nu à la place de la carte (taille identique).
 */
export async function handOffSeconds(card, block, animate) {
  const options = withMotion(HANDOFF, animate);
  const label = block.querySelector("[data-duration-label]");
  const animations = [card.animate([{ "--flip-surface": "transparent" }], options)];
  if (label) {
    const gap = Number.parseFloat(getComputedStyle(block).rowGap) || 0;
    label.style.overflow = "hidden";
    animations.push(
      label.animate(
        [
          { opacity: 1, height: `${label.offsetHeight}px`, marginTop: "0px" },
          { opacity: 0, height: "0px", marginTop: `${-gap}px` },
        ],
        options
      )
    );
  }
  await Promise.all(animations.map((animation) => animation.finished));
  const finalNumber = document.createElement("span");
  finalNumber.className = FINAL_NUMBER_CLASS;
  finalNumber.style.transformOrigin = "center top";
  card.replaceWith(finalNumber);
  return finalNumber;
}
/** Au « 10 » : le chiffre grossit dans la place déjà réservée. */
export function enlargeFinalNumber(finalNumber, fontPx, animate) {
  finalNumber.animate(
    [{ fontSize: getComputedStyle(finalNumber).fontSize }, { fontSize: `${fontPx}px` }],
    withMotion(ENLARGE, animate)
  );
}
export function bounceFinalNumber(finalNumber) {
  finalNumber.animate(BOUNCE_KEYFRAMES, BOUNCE);
}
// --- Ouverture -------------------------------------------------------------------
const FADE_OUT = {
  duration: 500,
  easing: "ease-in",
  fill: "forwards",
};
// Entrée douce : délai après le fondu du timer, courbe "ease-out" très allongée.
// `fill: "both"` masque le message pendant le délai.
const FADE_IN = {
  duration: 1600,
  delay: 400,
  easing: EASE_OUT_SOFT,
  fill: "both",
};
// Redescente finale, lancée à la fin du fondu du timer.
const SETTLE = {
  duration: 1800,
  easing: EASE_IN_OUT_SOFT,
};
// Épilogue : le cercle s'efface, puis le logo grandit au-delà de la place du
// cercle (logo 50 % → 125 % du cadre).
const LOGO_FINAL_SCALE = 2.5;
const RING_OUT = {
  duration: 900,
  easing: "ease-out",
  fill: "forwards",
};
const LOGO_GROW = {
  duration: 1400,
  easing: EASE_OUT_SOFT,
  fill: "forwards",
};
/**
 * Timer → message, puis épilogue. `motion` à false (mouvement réduit) : seuls
 * les fondus d'opacité sont joués, les déplacements et agrandissements sont
 * instantanés.
 */
export async function playOpening(parts, motion) {
  const { root, status, message, ringTrack, logo } = parts;
  await status.animate([{ opacity: 1 }, { opacity: 0 }], FADE_OUT).finished;
  // FLIP : on mesure la position (bloc monté), on retire la montée et on pose le
  // message à sa place finale, puis on anime l'écart. Le bloc redescend en
  // douceur, message compris, sans saut de mise en page.
  const topBefore = root.getBoundingClientRect().top;
  for (const animation of root.getAnimations()) animation.cancel();
  status.replaceChildren(message);
  const offset = topBefore - root.getBoundingClientRect().top;
  const rise = motion ? "translateY(0.75rem)" : "none";
  status.animate(
    [
      { opacity: 0, transform: rise },
      { opacity: 1, transform: "none" },
    ],
    FADE_IN
  );
  await root.animate([{ transform: `translateY(${offset}px)` }, { transform: "none" }], withMotion(SETTLE, motion))
    .finished;
  await ringTrack.animate([{ opacity: 1 }, { opacity: 0 }], RING_OUT).finished;
  logo.animate([{ transform: "scale(1)" }, { transform: `scale(${LOGO_FINAL_SCALE})` }], withMotion(LOGO_GROW, motion));
}
